import { useMemo, useRef } from "react";
import toast from "react-hot-toast";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download, X } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { copyToClipboard } from "@/utils/clipboard";

const slugify = (title) =>
  (title || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

/**
 * QR code for a job's candidate-facing link — scan-to-apply. Controlled by
 * the caller (open/onOpenChange), so it can be driven from either the
 * job-list row's Actions dropdown or the job-detail toolbar with no
 * knowledge of which trigger opened it.
 */
const ShareJobQrModal = ({ open, onOpenChange, jobId, jobTitle }) => {
  const canvasRef = useRef(null);

  const jobLink = useMemo(
    () => (jobId ? `${window.location.origin}/user/job-detail/${jobId}` : ""),
    [jobId]
  );

  const handleCopyQr = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // navigator.clipboard is only exposed in a secure context (https, or a
    // literal "localhost"/"127.0.0.1" host) — over plain http on any other
    // hostname it's simply undefined, which is a connection issue, not a
    // browser-support one. Distinguish the two so the message is accurate.
    if (!window.isSecureContext) {
      toast.error("Copying images requires a secure (https) connection — use Download instead");
      return;
    }
    if (!navigator.clipboard?.write || typeof window.ClipboardItem === "undefined") {
      toast.error("Copying images isn't supported in this browser — use Download instead");
      return;
    }

    // navigator.clipboard.write must be called synchronously within the click
    // handler to keep the browser's user-activation grant — canvas.toBlob is
    // itself async, so its result is handed in as a pending Promise<Blob>
    // rather than awaited first, which is what ClipboardItem is built to
    // accept for exactly this case.
    const blobPromise = new Promise((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))), "image/png");
    });

    try {
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blobPromise })]);
      toast.success("QR code copied");
    } catch {
      toast.error("Couldn't copy the QR code. Please try Download instead.");
    }
  };

  const handleDownloadQr = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL("image/png");
    const filename = `${slugify(jobTitle) || "job"}-qr.png`;

    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    a.click();
  };

  const handleCopyLink = async () => {
    if (!jobLink) return;
    const ok = await copyToClipboard(jobLink);
    if (ok) toast.success("Link copied");
    else toast.error("Could not copy — copy manually");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader className="relative pr-10">
          <DialogTitle>Share this job</DialogTitle>
          <DialogDescription>
            {jobTitle ? `Copy or download the QR code, or copy the link, to share the "${jobTitle}" job with candidates.` : "Copy or download the QR code, or copy the link, to share this job with candidates."}
          </DialogDescription>
          <DialogClose asChild>
            <span className="absolute right-5 top-5 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center cursor-pointer shrink-0">
              <X size={16} strokeWidth={3} />
            </span>
          </DialogClose>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row gap-6 p-6">
          {/* Fixed white/black regardless of theme — a QR code's scannability
              depends on true contrast, not the app's light/dark palette. */}
          <div className="bg-text-base p-3 rounded-lg self-center sm:self-start shrink-0">
            <QRCodeCanvas ref={canvasRef} value={jobLink} size={180} level="H" />
          </div>

          <div className="flex-1 min-w-0 flex flex-col gap-5">
            <div className="flex flex-col gap-2.5">
              <p className="text-xs font-medium text-text-secondary">Save this code</p>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleCopyQr}
                  className="flex-1 text-xs border border-border-primary text-text-primary bg-background-secondary rounded-lg px-4 py-2 flex items-center justify-center gap-1.5"
                >
                  <Copy size={14} /> Copy QR
                </Button>
                <Button
                  onClick={handleDownloadQr}
                  className="flex-1 text-xs bg-button-secondary text-text-base rounded-lg px-4 py-2 flex items-center justify-center gap-1.5"
                >
                  <Download size={14} /> Download QR
                </Button>
              </div>
            </div>

            <div className="border-t border-border-primary pt-5 flex flex-col gap-2.5">
              <p className="text-xs font-medium text-text-secondary">Job link</p>
              <div className="flex items-stretch gap-2">
                <Input
                  type="text"
                  readOnly
                  value={jobLink}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 py-2 text-[12px] rounded-md bg-background-primary/40"
                />
                <Button
                  onClick={handleCopyLink}
                  className="px-3 py-2 rounded-md bg-button-secondary text-text-base text-[12px] flex items-center gap-1.5 shrink-0"
                >
                  <Copy size={14} /> Copy
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareJobQrModal;
